import { zodResolver } from "@hookform/resolvers/zod"
import { AlertTriangle, Pencil, Save, X } from "lucide-react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import type { IntakeLoad } from "@/features/intake/api/types"
import { SectionCard } from "@/features/intake/components/section-card"
import { useSaveSampling } from "@/features/intake/hooks/use-intake-mutations"
import { formatNumber } from "@/lib/format"

const nonNegativeInt = z
  .union([z.string(), z.number()])
  .transform((v) => Number(v))
  .refine((n) => !Number.isNaN(n) && n >= 0 && Number.isInteger(n))

const samplingSchema = z.object({
  bagsWet: nonNegativeInt,
  otherRejected: nonNegativeInt,
})

type SamplingFormInput = z.input<typeof samplingSchema>
type SamplingFormOutput = z.output<typeof samplingSchema>

interface SamplingCardProps {
  load: IntakeLoad
  isEditing: boolean
  onEditingChange: (editing: boolean) => void
  // When true, the card stays in saved/display mode and the Edit
  // affordance is hidden. Used when the load is COMPLETED or when the
  // user is inspecting this stage from the stepper.
  readOnly?: boolean
}

function asInputNumber(v: number | null | undefined): number {
  return (v ?? "") as unknown as number
}

interface LadderRowProps {
  label: string
  value: number
  tone?: "neutral" | "deduct" | "accent"
}

function LadderRow({ label, value, tone = "neutral" }: LadderRowProps) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span
        className={tone === "accent" ? "font-medium" : "text-muted-foreground"}
      >
        {label}
      </span>
      <span
        className={
          "tabular-nums " +
          (tone === "deduct"
            ? "text-red-600 dark:text-red-400"
            : tone === "accent"
              ? "font-semibold"
              : "")
        }
      >
        {tone === "deduct" ? "−" : ""}
        {formatNumber(value)}
      </span>
    </div>
  )
}

export function SamplingCard({
  load,
  isEditing,
  onEditingChange,
  readOnly = false,
}: SamplingCardProps) {
  const { t } = useTranslation(["intake", "common"])
  const saveSampling = useSaveSampling(load.id)
  const sampling = load.sampling
  const saved = !!sampling
  // Read-only view always renders the saved values, never the form.
  const showForm = !readOnly && (!saved || isEditing)

  const form = useForm<SamplingFormInput, unknown, SamplingFormOutput>({
    resolver: zodResolver(samplingSchema),
    defaultValues: {
      bagsWet: asInputNumber(sampling?.bagsWet),
      otherRejected: asInputNumber(sampling?.otherRejected),
    },
  })

  // When entering edit mode (or when persisted data updates), refill the form
  // with the saved values so Cancel reverts cleanly.
  useEffect(() => {
    if (showForm) {
      form.reset({
        bagsWet: asInputNumber(sampling?.bagsWet),
        otherRejected: asInputNumber(sampling?.otherRejected),
      })
    }
  }, [showForm, sampling?.bagsWet, sampling?.otherRejected, form])

  const v = form.watch()
  const bagsUnloaded = load.bagsUnloaded ?? 0
  const bagsWet = showForm
    ? Number(v.bagsWet) || 0
    : (sampling?.bagsWet ?? 0)
  const otherRejected = showForm
    ? Number(v.otherRejected) || 0
    : (sampling?.otherRejected ?? 0)
  const rejected = bagsWet + otherRejected
  const accepted = showForm
    ? Math.max(0, bagsUnloaded - rejected)
    : (sampling?.acceptedBags ?? Math.max(0, bagsUnloaded - rejected))
  const overRejected = showForm && rejected > bagsUnloaded

  const ready =
    !overRejected &&
    v.bagsWet !== "" &&
    v.bagsWet !== undefined &&
    v.otherRejected !== "" &&
    v.otherRejected !== undefined

  const onSubmit = form.handleSubmit(async (parsed) => {
    if (overRejected) return
    await saveSampling.mutateAsync({
      bagsWet: parsed.bagsWet,
      otherRejected: parsed.otherRejected,
    })
    onEditingChange(false)
  })

  const handleCancel = () => {
    form.reset({
      bagsWet: asInputNumber(sampling?.bagsWet),
      otherRejected: asInputNumber(sampling?.otherRejected),
    })
    onEditingChange(false)
  }

  const labelClass = "text-[10px] font-medium uppercase tracking-wide"

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <SectionCard
          kicker={t("sampling.kicker")}
          title={t("sampling.title")}
          subtitle={t("sampling.subtitle")}
          roleBadge={t("detail.role.lab")}
          state={sampling ? "saved" : "default"}
        >
          <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
            <div className="space-y-4">
              {showForm ? (
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="bagsWet"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelClass}>
                          {t("sampling.wetBags")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="0"
                            className="h-9"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="otherRejected"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelClass}>
                          {t("sampling.otherRejected")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="0"
                            className="h-9"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <ReadOnlyField
                    label={t("sampling.wetBags")}
                    value={formatNumber(bagsWet)}
                  />
                  <ReadOnlyField
                    label={t("sampling.otherRejected")}
                    value={formatNumber(otherRejected)}
                  />
                </div>
              )}

              {showForm && overRejected ? (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 size-3.5 flex-shrink-0" />
                  <span>
                    {t("sampling.overRejected", {
                      rejected,
                      unloaded: bagsUnloaded,
                    })}
                  </span>
                </div>
              ) : null}

              {showForm ? (
                <p className="text-xs text-muted-foreground">
                  {t("sampling.helper")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1 rounded-md border bg-muted/30 p-4">
              <div className="mb-2 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                {t("sampling.ladderHeader")}
              </div>
              <LadderRow
                label={t("sampling.ladder.unloaded")}
                value={bagsUnloaded}
              />
              <LadderRow
                label={t("sampling.ladder.wet")}
                value={bagsWet}
                tone="deduct"
              />
              <LadderRow
                label={t("sampling.ladder.otherRejected")}
                value={otherRejected}
                tone="deduct"
              />
              <div className="my-1 border-t border-dashed border-border/60" />
              <LadderRow
                label={t("sampling.ladder.accepted")}
                value={accepted}
                tone="accent"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {showForm ? (
              <>
                {saved ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={saveSampling.isPending}
                  >
                    <X className="size-4" />
                    {t("common:actions.cancel")}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  disabled={!ready || saveSampling.isPending}
                >
                  <Save className="size-4" />
                  {saved ? t("sampling.updateCta") : t("sampling.saveCta")}
                </Button>
              </>
            ) : readOnly ? null : (
              <Button
                type="button"
                variant="outline"
                onClick={() => onEditingChange(true)}
              >
                <Pencil className="size-4" />
                {t("sampling.editCta")}
              </Button>
            )}
          </div>
        </SectionCard>
      </form>
    </Form>
  )
}

interface ReadOnlyFieldProps {
  label: string
  value: string
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="bg-muted/40 rounded-md border px-3 py-2 text-sm font-medium tabular-nums">
        {value}
      </div>
    </div>
  )
}
